import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, LogOut, Download, Users, ArrowLeft, Quote, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BrandHeader from "@/components/BrandHeader";
import { adminLogin, fetchLeads, updateLead, fetchFieldContent, updateFieldContent } from "@/lib/apiClient";

const TOKEN_KEY = "fenance_admin_token";
const STATUS_OPTIONS = ["Neu", "Kontaktiert", "Termin", "Kunde", "Kein Interesse"];
const inputCls =
  "h-12 rounded-none bg-[#111] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-brand-orange";

const zoneColor = (label) =>
  ({ Blindflug: "#ef4444", Sichtflug: "#f18e00", Instrumentenflug: "#22c55e" }[label] || "#808080");

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso || "";
  }
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { token } = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="inline-flex items-center gap-2 border border-brand-orange/40 bg-brand-orange/10 px-3 py-1.5 mb-6">
        <Lock className="h-3.5 w-3.5 text-brand-orange" />
        <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">Admin-Bereich</span>
      </div>
      <h1 className="font-display uppercase text-2xl tracking-tight text-white">Lead-Übersicht</h1>
      <p className="mt-3 text-sm text-white/50">Bitte melden Sie sich an.</p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-white/50">E-Mail</Label>
          <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-white/50">Passwort</Label>
          <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        </div>
        {error && <p className="text-xs text-red-400" data-testid="admin-error">{error}</p>}
        <Button type="submit" data-testid="admin-login-btn" disabled={loading}
          className="w-full h-12 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-sm transition-colors">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
        </Button>
      </form>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/40 hover:text-brand-orange transition-colors">
        <ArrowLeft className="h-4 w-4" /> Zur Startseite
      </Link>
    </div>
  );
}

function LeadsTable({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchLeads(token)
      .then(setData)
      .catch((err) => {
        if (err?.response?.status === 401) {
          toast.error("Sitzung abgelaufen.");
          onLogout();
        } else {
          toast.error("Leads konnten nicht geladen werden.");
        }
      })
      .finally(() => setLoading(false));
  }, [token, onLogout]);

  const rowVal = (l, key) => (edits[l.id]?.[key] ?? l[key] ?? (key === "lead_status" ? "Neu" : ""));
  const setEdit = (id, key, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [key]: value } }));

  const saveRow = async (l) => {
    setSavingId(l.id);
    try {
      await updateLead(token, l.id, {
        status: rowVal(l, "lead_status"),
        note: rowVal(l, "lead_note"),
      });
      setData((d) => ({
        ...d,
        leads: d.leads.map((x) => x.id === l.id
          ? { ...x, lead_status: rowVal(l, "lead_status"), lead_note: rowVal(l, "lead_note") } : x),
      }));
      toast.success("Gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingId(null);
    }
  };

  const csv = useMemo(() => {
    if (!data?.leads?.length) return "";
    const rows = data.leads.map((l) => {
      const notes = {};
      l.fields.forEach((f) => { notes[f.id] = f.note; });
      return [
        fmtDate(l.created_at), l.contact.company || "", l.contact.name || "", l.contact.email || "",
        l.revenue || "", l.overall_note, l.overall_zone_label, l.report_requested_at ? "ja" : "",
        l.lead_status || "Neu", (l.lead_note || "").replace(/\n/g, " "),
        notes[1], notes[2], notes[3], notes[4], notes[5], notes[6], notes[7],
      ].map((x) => {
        let s = String(x ?? "");
        if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
        return `"${s.replace(/"/g, '""')}"`;
      }).join(";");
    });
    const head = ["Datum", "Firma", "Name", "E-Mail", "Umsatz", "Gesamtnote", "Zone", "Report angefragt",
      "Status", "Notiz", "HF1", "HF2", "HF3", "HF4", "HF5", "HF6", "HF7"];
    return [head.join(";"), ...rows].join("\n");
  }, [data]);

  const downloadCsv = () => {
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fenance-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="admin-leads">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-orange" />
            <h1 className="font-display uppercase text-2xl tracking-tight text-white">Lead-Übersicht</h1>
          </div>
          <p className="mt-1 text-sm text-white/50">{data?.count || 0} Auswertungen</p>
        </div>
        <div className="flex gap-3">
          <Button data-testid="admin-csv-btn" onClick={downloadCsv} disabled={!csv}
            className="h-11 px-5 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-xs transition-colors">
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
          <Button data-testid="admin-logout-btn" onClick={onLogout} variant="outline"
            className="h-11 px-5 rounded-none border-white/20 text-white/70 hover:bg-white/5 bg-transparent uppercase tracking-wider text-xs">
            <LogOut className="mr-2 h-4 w-4" /> Abmelden
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-black text-white/60">
            <tr className="border-b border-white/10">
              {["Datum", "Firma", "Name", "E-Mail", "Note", "Zone", "Felder (HF1–7)", "Status", "Notiz"].map((h) => (
                <th key={h} className="px-4 py-3 font-display text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.leads?.map((l, i) => (
              <tr key={l.id} className={`border-b border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`} data-testid="admin-lead-row">
                <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                <td className="px-4 py-3 text-white/90">{l.contact.company || <span className="text-white/25">—</span>}</td>
                <td className="px-4 py-3 text-white/70">{l.contact.name || <span className="text-white/25">—</span>}</td>
                <td className="px-4 py-3 text-white/70">
                  {l.contact.email ? (
                    <a href={`mailto:${l.contact.email}`} className="text-brand-orange hover:underline">{l.contact.email}</a>
                  ) : <span className="text-white/25">—</span>}
                </td>
                <td className="px-4 py-3 font-display text-white">{l.overall_note}</td>
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold" style={{ color: zoneColor(l.overall_zone_label) }}>
                    {l.overall_zone_label}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {l.fields.map((f) => (
                      <span key={f.id} title={f.name}
                        className="h-6 w-6 flex items-center justify-center text-[0.7rem] font-display"
                        style={{ backgroundColor: (f.note <= 2 ? "#22c55e" : f.note <= 4 ? "#f18e00" : "#ef4444"), color: "#000" }}>
                        {f.note}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid="admin-lead-status"
                    value={rowVal(l, "lead_status")}
                    onChange={(e) => setEdit(l.id, "lead_status", e.target.value)}
                    className="h-9 bg-[#111] border border-white/15 text-white text-xs px-2 focus:outline-none focus:border-brand-orange"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="admin-lead-note"
                      value={rowVal(l, "lead_note")}
                      onChange={(e) => setEdit(l.id, "lead_note", e.target.value)}
                      placeholder="Notiz…"
                      className="h-9 w-40 bg-[#111] border border-white/15 text-white text-xs px-2 placeholder:text-white/25 focus:outline-none focus:border-brand-orange"
                    />
                    <button
                      data-testid="admin-lead-save"
                      onClick={() => saveRow(l)}
                      disabled={savingId === l.id}
                      className="h-9 px-3 bg-brand-orange text-black text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-40"
                    >
                      {savingId === l.id ? "…" : "OK"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!data?.leads?.length && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-white/40">Noch keine Auswertungen.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContentEditor({ token }) {
  const [fields, setFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchFieldContent(token)
      .then((d) => setFields(d.fields))
      .catch(() => toast.error("Inhalte konnten nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [token]);

  const val = (f, key) => (edits[f.id]?.[key] ?? f.zitat?.[key] ?? "");
  const setEdit = (id, key, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [key]: value } }));

  const save = async (f) => {
    setSavingId(f.id);
    try {
      const zitat = { text: val(f, "text"), author: val(f, "author"), bezug: val(f, "bezug") };
      await updateFieldContent(token, f.id, zitat);
      setFields((fs) => fs.map((x) => (x.id === f.id ? { ...x, zitat } : x)));
      toast.success(`Zitat für „${f.name}" gespeichert.`);
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-content-editor">
      <p className="text-sm text-white/50">
        Zitate je Handlungsfeld – erscheinen als Einstieg auf den Playbook-Seiten, im PDF-Lagebericht und im LinkedIn-Post.
      </p>
      {fields?.map((f) => (
        <div key={f.id} className="border border-white/10 bg-card p-5" data-testid={`admin-zitat-${f.id}`}>
          <div className="flex items-center gap-2 mb-4">
            <span className="font-display text-brand-orange text-xl">{String(f.id).padStart(2, "0")}</span>
            <h3 className="font-display uppercase text-sm tracking-tight text-white">{f.name}</h3>
          </div>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-white/50">Zitat</Label>
              <Textarea
                data-testid={`admin-zitat-text-${f.id}`}
                value={val(f, "text")}
                onChange={(e) => setEdit(f.id, "text", e.target.value)}
                rows={3}
                className="rounded-none bg-[#111] border-white/15 text-white text-sm focus-visible:ring-1 focus-visible:ring-brand-orange resize-none"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-white/50">Autor</Label>
                <Input
                  data-testid={`admin-zitat-author-${f.id}`}
                  value={val(f, "author")}
                  onChange={(e) => setEdit(f.id, "author", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-white/50">Bezug (deutscher Begleitsatz)</Label>
              <Textarea
                data-testid={`admin-zitat-bezug-${f.id}`}
                value={val(f, "bezug")}
                onChange={(e) => setEdit(f.id, "bezug", e.target.value)}
                rows={2}
                className="rounded-none bg-[#111] border-white/15 text-white text-sm focus-visible:ring-1 focus-visible:ring-brand-orange resize-none"
              />
            </div>
            <div>
              <button
                data-testid={`admin-zitat-save-${f.id}`}
                onClick={() => save(f)}
                disabled={savingId === f.id}
                className="inline-flex items-center gap-2 h-10 px-5 bg-brand-orange text-black text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-40"
              >
                {savingId === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("leads");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen relative z-10">
      <BrandHeader minimal />
      {!token ? (
        <LoginForm onLogin={setToken} />
      ) : (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-10">
          <div className="flex gap-1 border-b border-white/10 mb-2">
            <button
              data-testid="admin-tab-leads"
              onClick={() => setTab("leads")}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wider transition-colors ${tab === "leads" ? "text-brand-orange border-b-2 border-brand-orange" : "text-white/50 hover:text-white"}`}
            >
              <Users className="h-4 w-4" /> Leads
            </button>
            <button
              data-testid="admin-tab-content"
              onClick={() => setTab("content")}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wider transition-colors ${tab === "content" ? "text-brand-orange border-b-2 border-brand-orange" : "text-white/50 hover:text-white"}`}
            >
              <Quote className="h-4 w-4" /> Zitate
            </button>
            <button
              data-testid="admin-tab-logout"
              onClick={logout}
              className="ml-auto flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wider text-white/40 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" /> Abmelden
            </button>
          </div>
          {tab === "leads" ? (
            <LeadsTable token={token} onLogout={logout} />
          ) : (
            <div className="py-8"><ContentEditor token={token} /></div>
          )}
        </div>
      )}
    </div>
  );
}
